import type { CityDto, GeoCoords } from '@/core/data/location/location.interface';
import { gambits } from '@/core/gambits';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useAuthStore } from './auth.store';
import { useGeolocationStore } from './geolocation.store';
import { useMapStore } from './map.store';
import { useSparkIntentStore } from './spark.intent';
import { useSparkStore } from './spark.store';

export const useCityStore = defineStore('city', () => {
  const authStore = useAuthStore();
  const { userDetails } = storeToRefs(authStore);

  // State

  const isError = ref<boolean>(false);
  const isLoading = ref<boolean>(false);
  const isUserConfirmed = ref<boolean>(false);

  const city = computed<CityDto | null>({
    get: () => userDetails.value?.city || null,
    set: (newCity) => {
      authStore.setUserCity(newCity);
    },
  });

  const isInitialized = computed((): boolean => {
    return !!city.value?.id;
  });

  const hasSparkableLocations = computed((): boolean => {
    return !!city.value?.sparkableLocationIds?.length;
  });

  const googlePlaceId = computed((): string | null => {
    return city.value?.googlePlaceId || null;
  });

  function setCity(newCity: CityDto | null) {
    authStore.setUserCity(newCity);
  }

  function confirmCity() {
    isUserConfirmed.value = true;
  }

  // Global stores

  const geolocationStore = useGeolocationStore();
  const { userLocation } = storeToRefs(geolocationStore);
  const mapStore = useMapStore();
  const { currentLocation } = storeToRefs(mapStore);

  async function determineCity(coordinates?: GeoCoords) {
    if (!authStore.isAuthenticated()) {
      isError.value = true;
      return;
    }

    const isExplicitSelection = !!coordinates;
    if (!isExplicitSelection && isUserConfirmed.value) return;

    const targetCoordinates = coordinates || userLocation.value?.location || currentLocation.value;

    if (!targetCoordinates) {
      isError.value = true;
      return;
    }

    isError.value = false;
    isLoading.value = true;

    const updatedCity = await authStore.updateUserCity(targetCoordinates);

    isLoading.value = false;

    if (updatedCity?.id) {
      isError.value = false;
      if (isExplicitSelection) {
        isUserConfirmed.value = true;
        await gambits.sparkService.initializeAvailabilityHorizon(updatedCity.id).catch(() => {});
        await authStore.fetchUserInfo(true);

        const intentStore = useSparkIntentStore();
        const sparkStore = useSparkStore();
        intentStore.clearIntentStore();
        await sparkStore.fetchIntents({ refreshExisting: true });
      }
    } else {
      isError.value = true;
    }
  }

  watch(
    () => [userDetails.value?.city?.id, userLocation.value?.location, currentLocation.value],
    ([userCityId, userCoords, mapCoords]) => {
      if (userCityId) return;
      if (!authStore.isAuthenticated()) return;
      if (!userCoords && !mapCoords) return;
      if (isInitialized.value) return;
      if (isLoading.value) return;
      if (isUserConfirmed.value) return;

      void determineCity();
    },
    { immediate: true },
  );

  const getCityBounds = () => {
    if (typeof google === 'undefined' || typeof google?.maps?.LatLngBounds === 'undefined') {
      return null;
    }

    if (
      !city.value ||
      !city.value.viewportHigh?.lat ||
      !city.value.viewportHigh?.lng ||
      !city.value.viewportLow?.lat ||
      !city.value.viewportLow?.lng ||
      !google.maps.LatLngBounds
    ) {
      return null;
    }

    const newBounds = new google.maps.LatLngBounds({
      north: city.value.viewportHigh.lat,
      south: city.value.viewportLow.lat,
      east: city.value.viewportHigh.lng,
      west: city.value.viewportLow.lng,
    });

    return newBounds;
  };

  const cityBounds = computed((): google.maps.LatLngBounds | null => {
    if (typeof google === 'undefined' || typeof google?.maps?.LatLngBounds === 'undefined') {
      return null;
    }

    if (
      !city.value ||
      !city.value.viewportHigh?.lat ||
      !city.value.viewportHigh?.lng ||
      !city.value.viewportLow?.lat ||
      !city.value.viewportLow?.lng ||
      !google.maps.LatLngBounds
    ) {
      return null;
    }

    const newBounds = new google.maps.LatLngBounds({
      north: city.value.viewportHigh.lat,
      south: city.value.viewportLow.lat,
      east: city.value.viewportHigh.lng,
      west: city.value.viewportLow.lng,
    });

    return newBounds;
  });

  return {
    city,
    hasSparkableLocations,
    googlePlaceId,
    cityBounds,
    getCityBounds,
    setCity,
    confirmCity,
    isInitialized,
    isUserConfirmed,
    determineCity,
    isLoading,
    isError,
  };
});
